import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Typography, Pagination, Spin, Row, Col, Select } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { getAllTaxes, resetAllTaxes } from '../../actions/orderActions'
import TaxCard from '../../components/TaxCard'
import { CustomInput } from '../../components/ui/CustomInput'
import { CustomButton } from '../../components/ui/CustomButton'
import Colors from '../../utils/colors'
import Constants from '../../utils/constants'
import { removeUnderScoreAndCapitalize } from '../../helpers/strings'

const { Title } = Typography
const { Option } = Select

const SearchOrders = () => {
  const dispatch = useDispatch()
  const { taxes, loading, totalTaxes } = useSelector((state) => state.allTaxes)
  const [page, setPage] = useState(1)
  const [perPage] = useState(50)
  const [hasSearched, setHasSearched] = useState(false)
  const [filters, setFilters] = useState({
    taxMode: '',
    mobileNumber: '',
    vehicleNumber: ''
  })

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  const handleSearch = () => {
    const searchFilters = {}
    if (filters.taxMode) searchFilters.category = filters.taxMode
    if (filters.mobileNumber) searchFilters.mobileNumber = filters.mobileNumber
    if (filters.vehicleNumber) searchFilters.vehicleNumber = filters.vehicleNumber

    dispatch(getAllTaxes({
      ...searchFilters,
      status: [Constants.ORDER_STATUS.CONFIRMED, Constants.ORDER_STATUS.CLOSED, Constants.ORDER_STATUS.CANCELLED],
      page: 1,
      perPage
    }))
    setPage(1)
    setHasSearched(true)
  }

  useEffect(() => {
    dispatch(resetAllTaxes())
  }, [])

  const handleReset = () => {
    setFilters({
      taxMode: '',
      mobileNumber: '',
      vehicleNumber: ''
    })
    setHasSearched(false)
    setPage(1)
    dispatch(resetAllTaxes())
  }

  const handlePageChange = (newPage) => {
    setPage(newPage)
    
    const searchFilters = {}
    if (filters.taxMode) searchFilters.category = filters.taxMode
    if (filters.mobileNumber) searchFilters.mobileNumber = filters.mobileNumber
    if (filters.vehicleNumber) searchFilters.vehicleNumber = filters.vehicleNumber

    dispatch(getAllTaxes({
      ...searchFilters,
      status: [Constants.ORDER_STATUS.CONFIRMED, Constants.ORDER_STATUS.CLOSED, Constants.ORDER_STATUS.CANCELLED],
      page: newPage,
      perPage
    }))
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <Title level={4} style={{ color: Colors.primary, margin: 0 }}>
            Search Orders
          </Title>
          {hasSearched && (
            <Title level={5} style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>
              {totalTaxes || 0} Total Orders
            </Title>
          )}
        </div>
        
        <div style={{ 
          backgroundColor: Colors.white, 
          padding: '20px', 
          borderRadius: '8px', 
          border: `1px solid ${Colors.lightGray}`,
          marginBottom: '20px'
        }}>
          <Row gutter={[16, 16]} align="bottom">
            <Col xs={24} sm={5}>
              <div style={{ marginBottom: '8px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: Colors.primary }}>
                  Mode
                </label>
              </div>
              <Select
                placeholder="Select Mode"
                style={{ width: '100%', fontSize: '12px' }}
                value={filters.taxMode || undefined}
                onChange={(value) => handleFilterChange('taxMode', value)}
                allowClear
              >
                {Object.values(Constants.MODES).map(taxMode => (
                  <Option key={taxMode} value={taxMode}>
                    {removeUnderScoreAndCapitalize(taxMode)}
                  </Option>
                ))}
              </Select>
            </Col>
            
            <Col xs={24} sm={5}>
              <div style={{ marginBottom: '8px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: Colors.primary }}>
                  Mobile Number
                </label>
              </div>
              <CustomInput
                placeholder="Enter Mobile Number"
                value={filters.mobileNumber}
                onChange={(e) => handleFilterChange('mobileNumber', e.target.value)}
              />
            </Col>
            
            <Col xs={24} sm={5}>
              <div style={{ marginBottom: '8px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: Colors.primary }}>
                  Vehicle Number
                </label>
              </div>
              <CustomInput
                placeholder="Enter Vehicle Number"
                value={filters.vehicleNumber}
                onChange={(e) => handleFilterChange('vehicleNumber', e.target.value)}
              />
            </Col>
            
            <Col xs={24} sm={9} style={{ display: 'flex', gap: '8px' }}>
              <CustomButton
                icon={<SearchOutlined />}
                onClick={handleSearch}
                disabled={!filters.taxMode && !filters.mobileNumber && !filters.vehicleNumber}
                classNames='create-btn'
                style={{
                  width: 'auto',
                  height: '32px',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  color: (!filters.taxMode && !filters.mobileNumber && !filters.vehicleNumber) ? Colors.white : undefined
                }}
              >
                Search
              </CustomButton>
              
              <CustomButton
                onClick={handleReset}
                classNames="delete-btn"
                style={{
                  width: 'auto',
                  height: '32px',
                  paddingLeft: '16px',
                  paddingRight: '16px'
                }}
              >
                Reset
              </CustomButton>
            </Col>
          </Row>
        </div>
      </div>
      
      <Spin spinning={hasSearched && loading}>
        <Row gutter={[16, 16]}>
          {hasSearched && taxes?.map((tax) => (
            <Col xs={24} sm={12} md={8} lg={8} key={tax._id}>
              <TaxCard tax={tax} onRefresh={handleSearch} />
            </Col>
          ))}
        </Row>
        
        {!hasSearched && (
          <div style={{ textAlign: 'center', padding: '40px', color: Colors.darkGray }}>
            Use filters above to search for orders
          </div>
        )}
        
        {hasSearched && taxes?.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: Colors.darkGray }}>
            No orders found matching your search criteria
          </div>
        )}
      </Spin>
      
      {totalTaxes > 0 && (
        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <Pagination
            current={page}
            total={totalTaxes}
            pageSize={perPage}
            onChange={handlePageChange}
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} orders`}
            itemRender={(current, type, originalElement) => {
              if (type === 'page') {
                return (
                  <span style={{
                    backgroundColor: current === page ? Colors.primary : 'transparent',
                    color: current === page ? Colors.white : Colors.primary,
                    border: `1px solid ${Colors.primary}`,
                    padding: '4px 8px',
                    borderRadius: 4,
                    cursor: 'pointer'
                  }}>
                    {current}
                  </span>
                )
              }
              return originalElement
            }}
          />
        </div>
      )}
    </div>
  )
}

export default SearchOrders